import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getEmailSchedule,
  createEmailSchedule,
  updateEmailSchedule,
  deleteEmailSchedule,
  toggleEmailSchedule,
  sendEmailScheduleNow,
  testSendEmailSchedule,
  getEmailScheduleSends,
  getEmailSchedulesList,
} from './reportEmailScheduleApi';

export const createReportEmailScheduleHooks = ({ queryClient }) => {
  const useEmailSchedule = (reportId) =>
    useQuery({
      queryKey: ['emailSchedule', reportId],
      queryFn: () => getEmailSchedule(reportId),
      enabled: Boolean(reportId),
      retry: false,
    });

  const useCreateEmailSchedule = () =>
    useMutation({
      mutationFn: createEmailSchedule,
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ['emailSchedule', variables?.reportId] });
      },
    });

  const useUpdateEmailSchedule = () =>
    useMutation({
      mutationFn: updateEmailSchedule,
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ['emailSchedule', variables?.reportId] });
      },
    });

  const useDeleteEmailSchedule = () =>
    useMutation({
      mutationFn: deleteEmailSchedule,
      onSuccess: (_data, reportId) => {
        queryClient.invalidateQueries({ queryKey: ['emailSchedule', reportId] });
      },
    });

  const useToggleEmailSchedule = () =>
    useMutation({
      mutationFn: toggleEmailSchedule,
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ['emailSchedule', variables?.reportId] });
      },
    });

  const useSendEmailScheduleNow = () =>
    useMutation({
      mutationFn: sendEmailScheduleNow,
      onSuccess: (_data, reportId) => {
        queryClient.invalidateQueries({ queryKey: ['emailScheduleSends', reportId] });
      },
    });

  const useTestSendEmailSchedule = () =>
    useMutation({
      mutationFn: testSendEmailSchedule,
      onSuccess: (_data, reportId) => {
        queryClient.invalidateQueries({ queryKey: ['emailScheduleSends', reportId] });
      },
    });

  const useEmailScheduleSends = (reportId, params = {}, options = {}) =>
    useQuery({
      // Avoid using an object in the queryKey; object identity changes cause cache misses/refetch loops.
      queryKey: [
        'emailScheduleSends',
        reportId || null,
        params?.status || null,
        params?.limit ?? null,
        params?.offset ?? null,
      ],
      queryFn: () => getEmailScheduleSends({ reportId, ...params }),
      enabled: (options.enabled ?? true) && Boolean(reportId),
    });

  const useEmailSchedulesList = (params = {}, options = {}) =>
    useQuery({
      // Avoid using an object in the queryKey; object identity changes cause cache misses/refetch loops.
      queryKey: ['emailSchedulesList', params?.activeOnly ?? null, params?.limit ?? null, params?.offset ?? null],
      queryFn: () => getEmailSchedulesList(params),
      enabled: options.enabled ?? true,
    });

  return {
    useEmailSchedule,
    useCreateEmailSchedule,
    useUpdateEmailSchedule,
    useDeleteEmailSchedule,
    useToggleEmailSchedule,
    useSendEmailScheduleNow,
    useTestSendEmailSchedule,
    useEmailScheduleSends,
    useEmailSchedulesList,
  };
};
